/* 
 * Copyright (C) 2022 SHC Holdings LLC.  All Rights Reserved. 
 */
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import {
  FilterDialog,
  DataYearTab,
  StatisticTab,
  SexTab,
  RaceTab,
  AgeTab,
  AggregatedDemographicTab,
  AggregatedSexTab,
  AggregatedRaceTab,
  AggregatedAgeTab,
  GeoLevelTab,
  ConsumptionGroupTab,
  useFilterEditing,
} from 'nmqf-common-ui';
import { ConditionSection, ConditionTab } from './conditiontab';

interface IndexFilterDialogProps {
  isOpen: boolean;
  toggle: () => void;
  defaultActiveTab?: string;
}

const validationSchema = Yup.object().shape({
  primaryConditionWithClaims: Yup.array().when('primaryConditionWithClaimsOptionals', {
    is: (primaryConditionWithClaimsOptionals) => primaryConditionWithClaimsOptionals?.length === 0,
    then: Yup.array()
      .nullable()
      .required('Please select at least one of the Primary Conditions')
      .min(1, 'Please select at least one of the Primary Conditions'),
  }),
  primaryConditionWithClaimsOptionals: Yup.array().when('primaryConditionWithClaims', {
    is: (primaryConditionWithClaims) => primaryConditionWithClaims?.length === 0,
    then: Yup.array()
      .nullable()
      .required('Please select at least one of the Primary Conditions')
      .min(1, 'Please select at least one of the Primary Conditions'),
  }),
}, [['primaryConditionWithClaims', 'primaryConditionWithClaimsOptionals']]);

export const IndexFilterDialog: React.FC<IndexFilterDialogProps> = (props) => {
  const filter = useFilterEditing()
  const filterCountDeps = useMemo(() => ({ consumptionGroupClasses: filter.consumptionGroupClasses, ages: filter.demographics?.ages }), [filter.consumptionGroupClasses, filter.demographics?.ages])
  return (
    <FilterDialog
      isOpen={props.isOpen}
      toggle={props.toggle}
      defaultActiveTab={props.defaultActiveTab || DataYearTab.defaultProps?.tabId}
      validationSchema={validationSchema}
    >
      <DataYearTab />
      <ConditionTab>
        <ConditionSection filterCountEffectDeps={[filterCountDeps.consumptionGroupClasses, filterCountDeps.ages]} />
      </ConditionTab>
      <ConsumptionGroupTab filterCountEnabled filterCountEffectDeps={[filterCountDeps.ages]} />
      <StatisticTab />
      <GeoLevelTab />
      <SexTab filterCountEffectDeps={[filterCountDeps.consumptionGroupClasses, filterCountDeps.ages]} />
      <RaceTab filterCountEffectDeps={[filterCountDeps.consumptionGroupClasses, filterCountDeps.ages]} />
      <AgeTab filterCountEnabled
        filterCountEffectDeps={[filterCountDeps.consumptionGroupClasses]}
      />
      <AggregatedDemographicTab />
      <AggregatedSexTab />
      <AggregatedRaceTab />
      <AggregatedAgeTab />
    </FilterDialog>
  );
};
